/**
 * Factory functions for creating injection tokens that depend on CRM services.
 *
 * This file is separated from tokens-and-interfaces.ts to avoid circular dependencies:
 * - tokens-and-interfaces.ts defines types and tokens
 * - This file imports those types/tokens AND the service implementations
 * - Services can import from tokens-and-interfaces.ts without creating a cycle
 */

import { inject } from '@angular/core';
import { Observable, of, switchMap } from 'rxjs';
import { Properties } from 'posthog-js';
import {
  GalaxyFilterChipDependencies,
  GalaxyFilterDefinitionInterface,
  GalaxyFilterInputOverride,
  GalaxyFilterInterface,
} from '@vendasta/galaxy/filter/chips';

// Import types and tokens from tokens-and-interfaces (no circular dependency)
import {
  CrmInjectionToken,
  CrmObjectInjectionToken,
  ActivityType,
  ObjectType,
  PageAnalyticsDependencies,
  FieldOptionsDependencies,
} from './tokens-and-interfaces';

// Import service implementations (these depend on tokens-and-interfaces, but we break the cycle)
import { CrmObjectService } from './shared-services/crm-services/crm-object-service/crm-object.service';
import { CrmFiltersService } from './shared-services/crm-services/crm-filters.service';
import { CRMTrackingService } from './shared-services/page-analytics.service';
import { OpportunityTableCustomizationService } from './shared-services/table-customization/opportunity-table-customization.service';
import { withDefaultCrmFilterOverrides } from './shared-components/id-filter/default-filter-overrides';

export function  tableFiltersInjectionTokenGenerator(
  objectType: ObjectType,
  activityType?: ActivityType,
  useUrlFilters = true,
): () => GalaxyFilterChipDependencies {
  if (!objectType) {
    console.error('objectType is required for tableFiltersInjectionTokenGenerator');
    return () => ({}) as GalaxyFilterChipDependencies;
  }

  return () => {
    const crmObjectService = inject(CrmObjectService);
    const crmFiltersService = inject(CrmFiltersService);
    const crmConfigDependencies = inject(CrmInjectionToken);
    const crmObjectDependencies = inject(CrmObjectInjectionToken, { optional: true });
    const deps: GalaxyFilterChipDependencies = {
      listObjectFilters(searchTerm: string): Observable<GalaxyFilterDefinitionInterface[]> {
        return crmFiltersService.listObjectFilters$(
          searchTerm,
          objectType,
          activityType,
          crmObjectDependencies?.objectSubtype$,
        );
      },
      getFieldOptions(fieldId: string, search: string): Observable<string[]> {
        return (crmObjectDependencies?.objectSubtype$ ?? of('')).pipe(
          switchMap((objectSubtype) =>
            crmObjectService.listFieldOptions(objectType, {
              fieldId: fieldId,
              searchTerm: search,
              crmObjectSubtype: objectSubtype,
            }),
          ),
        );
      },
      getFilterInputOverrides(): Observable<GalaxyFilterInputOverride[]> {
        const hostOverrides$ = ((): Observable<GalaxyFilterInputOverride[]> => {
          if (crmObjectDependencies) {
            return crmObjectDependencies.filterInputOverrides$ ?? of([] as GalaxyFilterInputOverride[]);
          }
          switch (objectType) {
            case 'Contact':
              return crmConfigDependencies.contact?.filterInputOverrides$ ?? of([] as GalaxyFilterInputOverride[]);
            case 'PotentialContact':
              return (
                crmConfigDependencies.potentialcontact?.filterInputOverrides$ ??
                crmConfigDependencies.contact?.filterInputOverrides$ ??
                of([] as GalaxyFilterInputOverride[])
              );
            case 'Company':
              return crmConfigDependencies.company?.filterInputOverrides$ ?? of([] as GalaxyFilterInputOverride[]);
            case 'Activity':
              switch (activityType) {
                case 'Task':
                  return crmConfigDependencies.task?.filterInputOverrides$ ?? of([] as GalaxyFilterInputOverride[]);
                default:
                  return (
                    crmConfigDependencies.activity?.filterInputOverrides$ ?? of([] as GalaxyFilterInputOverride[])
                  );
              }
            case 'Opportunity': {
              const opportunityCustomization = inject(OpportunityTableCustomizationService);
              const defaultOverrides = opportunityCustomization.getDefaultFilterOverrides();
              const externalOverrides$ = crmConfigDependencies.opportunity?.filterInputOverrides$ ?? of([]);
              return externalOverrides$.pipe(
                switchMap((externalOverrides) => of([...defaultOverrides, ...externalOverrides])),
              );
            }
            default:
              return of([] as GalaxyFilterInputOverride[]);
          }
        })();
        return withDefaultCrmFilterOverrides(hostOverrides$);
      },
      getInitialAppliedFilters(): Promise<GalaxyFilterInterface[]> {
        return crmFiltersService.getInitialAppliedFilters$(
          objectType,
          crmObjectDependencies?.objectSubtype$,
          activityType,
          useUrlFilters,
        );
      },
      setInitialAppliedFilters(filters: GalaxyFilterInterface[]): void {
        return crmFiltersService.setInitialFilters(filters);
      },
    };
    return deps;
  };
}

export function pageAnalyticsInjectionTokenGenerator(
  objectType: ObjectType,
  objectSubtype = '',
): () => PageAnalyticsDependencies {
  return () => {
    const crmTrackingService = inject(CRMTrackingService);
    const deps: PageAnalyticsDependencies = {
      trackEvent(action: string, properties?: Properties): void {
        if (objectSubtype) {
          crmTrackingService.trackEvent(`${objectType}-${objectSubtype}`, action, properties);
          return;
        }
        crmTrackingService.trackEvent(objectType, action, properties);
      },
    };
    return deps;
  };
}

export function dynamicListTableFiltersInjectionTokenGenerator(
  objectType: ObjectType,
  objectSubtype$: Observable<string>,
): () => GalaxyFilterChipDependencies {
  if (!objectType) {
    console.error('objectType is required for dynamicListTableFiltersInjectionTokenGenerator');
    return () => ({}) as GalaxyFilterChipDependencies;
  }

  return () => {
    const crmObjectService = inject(CrmObjectService);
    const crmFiltersService = inject(CrmFiltersService);
    const crmObjectDependencies = inject(CrmObjectInjectionToken, { optional: true });

    //only override objectSubtype for CustomObject
    const objectSubtypeObservable$ =
      objectType === 'CustomObject' ? objectSubtype$ : crmObjectDependencies?.objectSubtype$;

    const deps: GalaxyFilterChipDependencies = {
      listObjectFilters(searchTerm: string): Observable<GalaxyFilterDefinitionInterface[]> {
        return crmFiltersService.listObjectFilters$(searchTerm, objectType, undefined, objectSubtypeObservable$);
      },
      getFieldOptions(fieldId: string, search: string): Observable<string[]> {
        return (objectSubtypeObservable$ ?? of('')).pipe(
          switchMap((objectSubtype) =>
            crmObjectService.listFieldOptions(objectType, {
              fieldId: fieldId,
              searchTerm: search,
              crmObjectSubtype: objectSubtype,
            }),
          ),
        );
      },
      getFilterInputOverrides(): Observable<GalaxyFilterInputOverride[]> {
        return withDefaultCrmFilterOverrides(
          crmObjectDependencies?.filterInputOverrides$ ?? of([] as GalaxyFilterInputOverride[]),
        );
      },
      getInitialAppliedFilters(): Promise<GalaxyFilterInterface[]> {
        return crmFiltersService.getInitialAppliedFilters$(objectType, objectSubtypeObservable$, undefined, true);
      },
      setInitialAppliedFilters(filters: GalaxyFilterInterface[]): void {
        return crmFiltersService.setInitialFilters(filters);
      },
    };
    return deps;
  };
}

export function modelDrivenFormFieldOptionsInjectionTokenGenerator(
  objectType: ObjectType,
): () => FieldOptionsDependencies {
  return () => {
    const crmObjectService = inject(CrmObjectService);
    const crmObjectDependencies = inject(CrmObjectInjectionToken, { optional: true });
    const deps: FieldOptionsDependencies = {
      getFieldOptions(fieldId: string, search: string): Observable<string[]> {
        return (crmObjectDependencies?.objectSubtype$ ?? of('')).pipe(
          switchMap((objectSubtype) =>
            crmObjectService.listFieldOptions(objectType, {
              fieldId: fieldId,
              searchTerm: search,
              crmObjectSubtype: objectSubtype,
            }),
          ),
        );
      },
    };
    return deps;
  };
}
